import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Collapse,
  LinearProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  useMediaQuery,
} from '@mui/material';
import {
  Close,
  UploadFile,
  TableChart,
  ArrowForward,
  ArrowBack,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import { colors } from '../theme/colors';
import { flagEmoji } from '../utils/countryFlags';
import type { PlayerPoolEntry } from '../types/database';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ParsedExcelPlayer {
  name: string;
  country: string;
  gender: 'men' | 'women';
  price: number;
  seed: number;
  playerId: string;
  isDuplicate: boolean;
}

interface TennisExcelImportDialogProps {
  open: boolean;
  onClose: () => void;
  poolName: string;
  existingPlayerIds: string[];
  onImport: (players: Omit<PlayerPoolEntry, 'lastUpdated' | 'updatedBy'>[]) => Promise<void>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function displayFlag(code: string): string {
  if (!code || code === '---') return '🏳️';
  return flagEmoji(code) || '🏳️';
}

function displayCountry(code: string): string {
  return !code || code === '---' ? 'Neutral' : code;
}

function priceColor(price: number): string {
  if (price >= 11) return colors.orange.primary;
  if (price >= 8) return colors.blue.electric;
  return colors.grey[400];
}

function parsePlayers(file: File, existingIds: string[]): Promise<ParsedExcelPlayer[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        const parsed: ParsedExcelPlayer[] = [];
        let menSeedCounter = 1;
        let womenSeedCounter = 1;
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i] as unknown[];
          const name = String(row[0] ?? '').trim();
          const country = String(row[1] ?? '').trim();
          const genderRaw = String(row[2] ?? '').trim().toLowerCase();
          const priceRaw = row[3];

          if (!name) continue;

          const price = parseFloat(String(priceRaw)) || 0;
          const gender: 'men' | 'women' = genderRaw.startsWith('w') ? 'women' : 'men';
          const playerId = name
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '');

          // Auto-assign seed based on row order within each gender group
          const seed = gender === 'women' ? womenSeedCounter++ : menSeedCounter++;

          parsed.push({ name, country, gender, price, seed, playerId, isDuplicate: existingIds.includes(playerId) });
        }
        resolve(parsed);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

// ─── Sub-component: Player row ─────────────────────────────────────────────

const PlayerRow: React.FC<{ player: ParsedExcelPlayer; maxPrice: number }> = ({ player, maxPrice }) => {
  const pct = maxPrice > 0 ? (player.price / maxPrice) * 100 : 0;
  const col = priceColor(player.price);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 0.75,
        borderRadius: 1,
        opacity: player.isDuplicate ? 0.4 : 1,
        bgcolor: player.isDuplicate ? 'transparent' : alpha(colors.blue.navy, 0.25),
        '&:hover': { bgcolor: player.isDuplicate ? 'transparent' : alpha(colors.blue.navy, 0.45) },
      }}
    >
      {/* Seed badge */}
      <Box sx={{
        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
        bgcolor: alpha(colors.orange.primary, 0.12),
        border: `1px solid ${alpha(colors.orange.primary, 0.28)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: colors.orange.light }}>{player.seed}</Typography>
      </Box>

      {/* Flag + country */}
      <Box sx={{ minWidth: 52, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>{displayFlag(player.country)}</Typography>
        <Typography variant="caption" sx={{ color: player.country === '---' ? colors.grey[500] : colors.text.secondary, fontFamily: 'monospace', fontSize: '0.7rem' }}>
          {displayCountry(player.country)}
        </Typography>
      </Box>

      {/* Name */}
      <Typography sx={{ flex: 1, fontSize: '0.82rem', fontWeight: 500, color: colors.text.primary }} noWrap>
        {player.name}
        {player.isDuplicate && (
          <Chip label="already in pool" size="small" sx={{ ml: 1, fontSize: '0.6rem', height: 16, bgcolor: alpha(colors.grey[500], 0.2), color: colors.grey[400] }} />
        )}
      </Typography>

      {/* Price chip */}
      <Chip
        size="small"
        label={`${player.price} cr`}
        sx={{
          height: 20,
          fontSize: '0.7rem',
          fontWeight: 700,
          bgcolor: alpha(col, 0.15),
          color: col,
          border: `1px solid ${alpha(col, 0.3)}`,
          minWidth: 54,
        }}
      />

      {/* Price bar */}
      <Box sx={{ width: 72, flexShrink: 0 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 5,
            borderRadius: 3,
            bgcolor: alpha(colors.grey[700], 0.4),
            '& .MuiLinearProgress-bar': { bgcolor: col, borderRadius: 3 },
          }}
        />
      </Box>
    </Box>
  );
};

// ─── Sub-component: Gender section ────────────────────────────────────────────

const GenderSection: React.FC<{ label: string; accentColor: string; players: ParsedExcelPlayer[] }> = ({
  label, accentColor, players,
}) => {
  const total = players.reduce((s, p) => s + p.price, 0);
  const maxPrice = Math.max(...players.map(p => p.price), 0);
  const sorted = [...players].sort((a, b) => b.price - a.price);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: accentColor }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {players.length} players · {total.toFixed(1)} cr total
        </Typography>
      </Box>
      <Box
        sx={{
          border: `1px solid ${colors.border.subtle}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          bgcolor: alpha(colors.blue.navy, 0.2),
        }}
      >
        {sorted.map((p, i) => (
          <React.Fragment key={p.playerId}>
            <PlayerRow player={p} maxPrice={maxPrice} />
            {i < sorted.length - 1 && <Divider sx={{ borderColor: colors.border.subtle, opacity: 0.4 }} />}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

// ─── Main Dialog ──────────────────────────────────────────────────────────────

const TennisExcelImportDialog: React.FC<TennisExcelImportDialogProps> = ({
  open,
  onClose,
  poolName,
  existingPlayerIds,
  onImport,
}) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [step, setStep] = useState<'upload' | 'preview'>('upload');
  const [players, setPlayers] = useState<ParsedExcelPlayer[]>([]);
  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formatExpanded, setFormatExpanded] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    setStep('upload');
    setPlayers([]);
    setFileName('');
    setParsing(false);
    setImporting(false);
    setError(null);
    setFormatExpanded(false);
    setPendingFile(null);
    onClose();
  };

  const loadFile = useCallback((file: File) => {
    if (!file.name.endsWith('.xlsx')) {
      setError('Please upload an .xlsx file.');
      return;
    }
    setFileName(file.name);
    setPendingFile(file);
    setError(null);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) loadFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) loadFile(file);
  };

  const handleParse = async () => {
    if (!pendingFile) return;
    setParsing(true);
    setError(null);
    try {
      const parsed = await parsePlayers(pendingFile, existingPlayerIds);
      if (parsed.length === 0) {
        setError('No players found. Check the file has data in columns A–D starting from row 2.');
        return;
      }
      setPlayers(parsed);
      setStep('preview');
    } catch {
      setError('Failed to parse the Excel file. Make sure it is a valid .xlsx file.');
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    const toImport = players.filter(p => !p.isDuplicate);
    if (toImport.length === 0) return;
    setImporting(true);
    setError(null);
    try {
      await onImport(
        toImport.map(p => ({
          playerId: p.playerId,
          name: p.name,
          team: p.country === '---' ? 'Neutral' : p.country,
          role: p.gender as 'men' | 'women',
          points: 0,
          isOverseas: false,
          gender: p.gender,
          price: p.price,
          seed: p.seed,
        }))
      );
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed. Please try again.');
      setImporting(false);
    }
  };

  // ── Derived stats ──
  const men = players.filter(p => p.gender === 'men');
  const women = players.filter(p => p.gender === 'women');
  const importable = players.filter(p => !p.isDuplicate);
  const dupCount = players.filter(p => p.isDuplicate).length;
  const totalCredits = players.reduce((s, p) => s + p.price, 0);
  const maxPrice = Math.max(...players.map(p => p.price), 0);
  const avgPrice = players.length ? totalCredits / players.length : 0;

  const dialogSx = {
    '& .MuiDialog-paper': {
      background: colors.gradients.hero,
      border: `1px solid ${colors.border.default}`,
      borderRadius: 2,
      maxWidth: 680,
      width: '100%',
    },
  };

  return (
    <Dialog open={open} onClose={handleClose} sx={dialogSx} maxWidth="md" fullWidth fullScreen={isMobile}>
      {/* Title */}
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', pb: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold" color="white">
            Upload Tennis Pool from Excel
          </Typography>
          {poolName && (
            <Typography variant="caption" color="text.secondary">
              Pool: {poolName}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary', mt: -0.5 }}>
          <Close />
        </IconButton>
      </DialogTitle>

      {/* Stepper */}
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 1 }}>
        <Stepper activeStep={step === 'upload' ? 0 : 1} sx={{ '& .MuiStepLabel-label': { color: 'text.secondary' } }}>
          <Step><StepLabel>Upload</StepLabel></Step>
          <Step><StepLabel>Preview & Import</StepLabel></Step>
        </Stepper>
      </Box>

      <DialogContent sx={{ pt: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* ── STEP 1: Upload ── */}
        {step === 'upload' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Drop zone */}
            <Box
              sx={{
                border: `2px dashed ${isDragOver ? colors.blue.electric : colors.border.default}`,
                borderRadius: 2,
                p: 5,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: isDragOver ? alpha(colors.blue.electric, 0.07) : alpha(colors.blue.navy, 0.3),
                transition: 'all 0.2s',
                '&:hover': { borderColor: colors.blue.electric, bgcolor: alpha(colors.blue.electric, 0.05) },
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
            >
              <UploadFile sx={{ fontSize: 52, color: isDragOver ? colors.blue.electric : colors.blue.light, mb: 1.5 }} />
              <Typography variant="h6" color="white" gutterBottom>
                Drop your Excel file here
              </Typography>
              <Typography variant="body2" color="text.secondary">
                or click to browse — accepts .xlsx files
              </Typography>
              {fileName && (
                <Chip
                  label={fileName}
                  icon={<TableChart sx={{ fontSize: '0.9rem !important' }} />}
                  sx={{ mt: 2, bgcolor: alpha(colors.blue.electric, 0.18), color: colors.blue.light, border: `1px solid ${alpha(colors.blue.electric, 0.3)}` }}
                />
              )}
              <input ref={fileInputRef} type="file" accept=".xlsx" hidden onChange={handleFileChange} />
            </Box>

            {/* Format guide */}
            <Box sx={{ border: `1px solid ${colors.border.subtle}`, borderRadius: 1, overflow: 'hidden' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, bgcolor: alpha(colors.blue.electric, 0.07), cursor: 'pointer' }}
                onClick={() => setFormatExpanded(v => !v)}
              >
                <Typography variant="body2" color="text.secondary">
                  Expected column format
                </Typography>
                <IconButton size="small" sx={{ color: 'text.secondary' }}>
                  {formatExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
              </Box>
              <Collapse in={formatExpanded}>
                <Box sx={{ px: 2, py: 1.5, bgcolor: alpha(colors.blue.navy, 0.4), overflowX: 'auto' }}>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                    <Box component="thead">
                      <Box component="tr">
                        {['A: Player Name', 'B: Country', 'C: Gender', 'D: Price'].map(h => (
                          <Box key={h} component="th" sx={{ textAlign: 'left', py: 0.5, pr: 2, color: colors.blue.light, fontWeight: 700 }}>
                            {h}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {[
                        ['Sinner Jannik', 'ITA', 'Men', '13.5'],
                        ['Swiatek Iga', 'POL', 'Women', '12.0'],
                        ['Medvedev Daniil', '---', 'Men', '9.5'],
                      ].map((row, i) => (
                        <Box component="tr" key={i}>
                          {row.map((cell, j) => (
                            <Box key={j} component="td" sx={{ py: 0.25, pr: 2, color: colors.text.secondary }}>
                              {cell}
                            </Box>
                          ))}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Country <code>---</code> = neutral flag (ITF-restricted nations). Row 1 is the header.
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          </Box>
        )}

        {/* ── STEP 2: Preview ── */}
        {step === 'preview' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Summary stat cards */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
                gap: 1,
                p: 1.5,
                bgcolor: alpha(colors.blue.navy, 0.4),
                borderRadius: 1.5,
                border: `1px solid ${colors.border.subtle}`,
              }}
            >
              {[
                { label: 'Players', value: players.length, color: colors.text.primary },
                { label: 'Men', value: men.length, color: colors.blue.light, prefix: '♂' },
                { label: 'Women', value: women.length, color: '#F06292', prefix: '♀' },
                { label: 'Total Credits', value: `${totalCredits.toFixed(1)} cr`, color: colors.orange.primary, sub: `avg ${avgPrice.toFixed(1)} · max ${maxPrice}` },
              ].map(({ label, value, color, prefix, sub }) => (
                <Box key={label} sx={{ textAlign: 'center', px: 0.5, py: 0.5 }}>
                  <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color, lineHeight: 1 }}>
                    {prefix ? `${prefix} ` : ''}{value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {label}
                  </Typography>
                  {sub && (
                    <Typography sx={{ fontSize: '0.6rem', color: colors.grey[500], mt: 0.25 }}>
                      {sub}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            {/* Duplicate warning */}
            {dupCount > 0 && (
              <Alert severity="warning" icon={<Warning fontSize="small" />} sx={{ py: 0.5 }}>
                {dupCount} player{dupCount !== 1 ? 's' : ''} already in pool — will be skipped on import.
              </Alert>
            )}

            {/* Player lists */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
              {men.length > 0 && (
                <GenderSection
                  label="♂ Men"
                  accentColor={colors.blue.light}
                  players={men}
                />
              )}
              {women.length > 0 && (
                <GenderSection
                  label="♀ Women"
                  accentColor="#F06292"
                  players={women}
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, gap: 1 }}>
        {step === 'upload' ? (
          <>
            <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
            <Button
              variant="contained"
              endIcon={parsing ? <CircularProgress size={16} color="inherit" /> : <ArrowForward />}
              onClick={handleParse}
              disabled={!pendingFile || parsing}
              sx={{ background: colors.gradients.button, '&:hover': { background: colors.blue.deep } }}
            >
              {parsing ? 'Parsing…' : 'Parse File'}
            </Button>
          </>
        ) : (
          <>
            <Button startIcon={<ArrowBack />} onClick={() => setStep('upload')} sx={{ color: 'text.secondary' }}>
              Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={importable.length === 0 || importing}
              startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
              sx={{ background: importable.length > 0 ? colors.gradients.button : undefined, '&:hover': { background: colors.blue.deep } }}
            >
              {importing ? 'Importing…' : `Import ${importable.length} player${importable.length !== 1 ? 's' : ''}`}
              {dupCount > 0 && !importing && (
                <Typography component="span" sx={{ fontSize: '0.65rem', ml: 0.75, opacity: 0.7 }}>
                  ({dupCount} skipped)
                </Typography>
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TennisExcelImportDialog;
